import React, { useMemo } from 'react';
import { AmplifierModalPortal } from './AmplifierModalPortal';
import type { RemotePopupType } from './amplifier';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import type { AmplifierLocaleKey } from '../../i18n/amplifier/locales';

export interface RemotePopupModalProps {
  open: boolean;
  message: string;
  popupType?: RemotePopupType;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const POPUP_UI_KEYS: Record<
  RemotePopupType,
  { title: AmplifierLocaleKey; confirmLabel: AmplifierLocaleKey; cancelLabel: AmplifierLocaleKey; readOnly: boolean; defaultMessage: AmplifierLocaleKey }
> = {
  pa_switch: {
    title: '功放开启确认',
    confirmLabel: '确定',
    cancelLabel: '取消',
    readOnly: false,
    defaultMessage: '设备请求确认开启功放。',
  },
  update: {
    title: '更新确认',
    confirmLabel: '确认更新',
    cancelLabel: '取消',
    readOnly: false,
    defaultMessage: '设备检测到可用更新，是否开始下载并安装？',
  },
  update_progress: {
    title: '更新进度',
    confirmLabel: '确定',
    cancelLabel: '取消',
    readOnly: true,
    defaultMessage: '正在更新…',
  },
  restart: {
    title: '重启确认',
    confirmLabel: '立即重启',
    cancelLabel: '稍后重启',
    readOnly: false,
    defaultMessage: '更新已安装完成，是否立即重启？',
  },
};

/** 协议 §5.7：按 popup_type 镜像设备弹窗，并将用户操作回传设备 */
export const RemotePopupModal: React.FC<RemotePopupModalProps> = ({
  open,
  message,
  popupType = 'pa_switch',
  pending = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useAmplifierI18n();
  const ui = useMemo(() => {
    const keys = POPUP_UI_KEYS[popupType] ?? POPUP_UI_KEYS.pa_switch;
    return {
      title: t(keys.title),
      confirmLabel: t(keys.confirmLabel),
      cancelLabel: t(keys.cancelLabel),
      readOnly: keys.readOnly,
      defaultMessage: t(keys.defaultMessage),
    };
  }, [popupType, t]);

  return (
    <AmplifierModalPortal open={open}>
      <div
        className={`amplifier-modal-panel remote-popup-modal${
          ui.readOnly ? ' remote-popup-modal--readonly' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remote-popup-title"
        aria-describedby="remote-popup-message"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="remote-popup-title" className="amplifier-modal-panel__title">
          {ui.title}
        </h3>
        <p id="remote-popup-message" className="remote-popup-modal__message">
          {message || ui.defaultMessage}
        </p>
        {!ui.readOnly && (
          <div className="remote-popup-modal__actions">
            <button
              type="button"
              className="amp-modal-btn"
              disabled={pending}
              onClick={onCancel}
            >
              <span className="amp-modal-btn__text">{pending ? t('处理中…') : ui.cancelLabel}</span>
            </button>
            <button
              type="button"
              className="amp-modal-btn amp-modal-btn--primary"
              disabled={pending}
              onClick={onConfirm}
            >
              <span className="amp-modal-btn__text">{pending ? t('处理中…') : ui.confirmLabel}</span>
            </button>
          </div>
        )}
      </div>
    </AmplifierModalPortal>
  );
};

export default RemotePopupModal;
